import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API } from "@/lib/api";
import { storage } from "@/lib/storage";
import { deviceId } from "@/lib/useLikes";
import { trackEvent } from "@/lib/analytics";

const KEY = "atlas.trip.group";
export const normalizeCode = (s) => { const c = (s || "").toUpperCase().replace(/[^A-Z0-9]/g, ""); const core = c.startsWith("ATLAS") ? c.slice(5) : c; return core.length === 4 ? `ATLAS-${core}` : ""; };
export const groupUrl = (g, lang) => `${window.location.origin}/itinerari?c=${g.country}&d=${g.days}&g=${g.code}&lang=${lang}`;
export const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";
export const MEMBER_COLORS = ["#C2552B", "#3B6FA0", "#2E7D4F", "#B8860B", "#5B4B8A", "#B03A5B", "#2A7F8F", "#7A5230"];

/** Travel group shared by code: membership persisted in localStorage, state polled every 20 s. */
export function useGroup() {
  const qc = useQueryClient();
  const [code, setCode] = useState(() => storage.get(KEY) || "");
  const query = useQuery({
    queryKey: ["group", code],
    queryFn: async () => (await axios.get(`${API}/groups/${code}`, { params: { device_id: deviceId() } })).data,
    enabled: Boolean(code), refetchInterval: 20000, retry: false,
  });
  const forget = useCallback(() => { storage.remove(KEY); setCode(""); }, []);
  useEffect(() => { if (query.error?.response?.status === 404) forget(); }, [query.error, forget]);
  const remember = useCallback((g) => { storage.set(KEY, g.code); qc.setQueryData(["group", g.code], g); setCode(g.code); return g; }, [qc]);

  const create = useCallback(async (name, country, days, visited = []) => {
    const { data } = await axios.post(`${API}/groups`, { device_id: deviceId(), name, country, days, visited });
    trackEvent("group_create", { code: country }); return remember(data);
  }, [remember]);
  const join = useCallback(async (c, name, visited = []) => {
    const { data } = await axios.post(`${API}/groups/${normalizeCode(c)}/join`, { device_id: deviceId(), name, visited });
    trackEvent("group_join", { code: data.country }); return remember(data);
  }, [remember]);
  const leave = useCallback(() => { if (code) axios.post(`${API}/groups/${code}/leave`, { device_id: deviceId() }).catch(() => {}); forget(); }, [code, forget]);
  const setVisited = useCallback((stop, visited) => {
    if (!code) return;
    axios.put(`${API}/groups/${code}/visited`, { device_id: deviceId(), stop, visited }).then(({ data }) => qc.setQueryData(["group", code], data)).catch(() => {});
  }, [code, qc]);

  const group = query.data && query.data.is_member ? query.data : null;
  return { code, group, create, join, leave, setVisited };
}

/** { stopId: [{ id, name, color }] } for members other than me. */
export const othersByStop = (group) => {
  const map = {};
  (group?.members || []).forEach((m, i) => { if (m.me) return; const color = MEMBER_COLORS[i % MEMBER_COLORS.length]; m.visited.forEach((s) => { (map[s] ||= []).push({ id: m.id, name: m.name, color }); }); });
  return map;
};
